import { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { PillButton } from '@ab-platform/ui/pill-button';
import { FocusedPageShell } from '@ab-platform/ui/focused-page-shell';
import { DocumentUploadStep } from '@/components/DocumentUploadStep';
import { IdentityVerificationSummary } from '@/components/IdentityVerificationSummary';
import {
  clearDocumentsPageStateFromSession,
  loadDocumentsPageStateFromAllSources,
  type DocumentsPageState,
} from '@/lib/documents-state-recovery';
import {
  buildSuccessPath,
  persistLastRequestId,
} from '@/lib/onboarding-success-nav';

/**
 * Step 5 — public document upload page.
 *
 * Recovery channels (tried in priority order; first hit wins):
 *
 *   1. router state — `location.state`. Primary path, wired by the
 *      Summary step's `navigate(...)`.
 *   2. URL hash     — `#s=<base64url(json)>`. Support-recovery channel
 *      for an operator-issued one-shot URL when state has been lost.
 *      The hash is stripped from the address bar after first read.
 *   3. sessionStorage — `ab-onboarding-public:documents-page-state:v1`.
 *      Silent in-tab resilience for hard refresh.
 *
 * On terminal events (success / session expired) we clear the
 * sessionStorage copy so a future visit to the page doesn't surface
 * stale state. The router state and URL hash auto-clear by design
 * (state is in-memory, hash is stripped on read).
 */
export function DocumentsPage(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  // useMemo with the location-identifying fields as deps so we only
  // re-run the composite-read once per actual location change. The
  // hash strip is a side effect inside the helper; React will re-run
  // it on every key change but the helper itself is idempotent (no-op
  // when hash is already absent).
  const state = useMemo<DocumentsPageState | null>(
    () =>
      loadDocumentsPageStateFromAllSources(location.state, window.location.hash),
    // Re-evaluate when the user navigates (key changes) or when the
    // hash changes. `location.state` is part of the same composite
    // navigation key, so `location.key` covers both.
    [location.key],
  );

  if (state === null) {
    return (
      <FocusedPageShell
        title="Sessione di caricamento scaduta"
        description="Per sicurezza la sessione è scaduta. Invia nuovamente la richiesta per ricevere un nuovo accesso."
        footer={
          <PillButton asChild size="lg" className="w-full">
            <Link to="/registrazione-intermediario">Torna al modulo</Link>
          </PillButton>
        }
      >
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <span
            className="flex h-12 w-12 items-center justify-center rounded-full bg-state-warning/10 text-state-warning"
            aria-hidden
          >
            <ShieldAlert className="h-6 w-6" />
          </span>
        </div>
      </FocusedPageShell>
    );
  }

  // Terminal: BE rejected the token (UPLOAD_TOKEN_INVALID). Drop every
  // recovery channel so the next render shows the expired panel — anything
  // else would loop the user back into a bad state. Shared by the documents
  // flow and the identity-verification card.
  const handleSessionExpired = () => {
    clearDocumentsPageStateFromSession();
    navigate('/registrazione-intermediario/documenti', {
      replace: true,
      state: null,
    });
  };

  return (
    <FocusedPageShell
      title="Allega i documenti"
      description="Formati accettati: PDF, JPEG, PNG. Dimensione massima: 10 MB. Una volta inviata, la richiesta passerà in revisione e ti contatteremo via email."
      footer={null}
    >
      {state.submissionId ? (
        <div className="grid gap-0.5 rounded-md bg-foreground/5 px-3 py-2 text-xs">
          <span className="text-muted-foreground">Riferimento richiesta</span>
          <span className="font-mono text-foreground">{state.submissionId}</span>
        </div>
      ) : null}
      {/* PR-DIDIT-6B: identity verification moved to its own step
          (/verifica-identita). Here we keep only a compact, read-only status
          line — no heavy CTAs. */}
      <IdentityVerificationSummary uploadToken={state.uploadToken} />
      <DocumentUploadStep
        uploadToken={state.uploadToken}
        legalNature={state.legalNature}
        orgType={state.orgType}
        onAllRequiredUploaded={() => {
          // Terminal: clear the sessionStorage copy so a future hit on
          // /documenti doesn't surface a stale (now-consumed) token.
          clearDocumentsPageStateFromSession();
          // Carry the reference id via URL + sessionStorage so the success
          // page survives a hard refresh. PR-ONBOARDING-SUCCESS-REF-PERSIST-1.
          persistLastRequestId(state.submissionId);
          navigate(buildSuccessPath(state.submissionId), {
            replace: true,
            state: { submissionId: state.submissionId },
          });
        }}
        onSessionExpired={handleSessionExpired}
      />
    </FocusedPageShell>
  );
}
